import { MeshBasicMaterial } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import { PropField } from './propField';
import { canopyTreeParts, palmParts, polytankGeometry, streetlightParts, umbrellaParts } from './propGeometries';

/** All instanced street props, shared by every road tile. */
export interface StreetProps {
  trees: PropField;
  palms: PropField;
  lights: PropField;
  umbrellas: PropField;
  tanks: PropField;
}

/** The street-lamp heads: dull grey by day, bright warm light at night (0..1). */
export const lampMaterial = new MeshBasicMaterial({ color: '#8d9399' });

export function setLampGlow(glow: number): void {
  lampMaterial.color.set('#8d9399').lerp(lampMaterial.color.clone().set('#ffe2a8'), glow);
}

export const PER_TILE = { trees: 16, palms: 4, lights: 1, umbrellas: 18, tanks: 12 } as const;

export function createStreetProps(tiles: number): StreetProps {
  const tree = canopyTreeParts();
  const palm = palmParts();
  const umbrella = umbrellaParts();
  const light = streetlightParts();
  return {
    trees: new PropField(
      [
        { geometry: tree.trunk, material: flatMaterial('#5b4636') },
        { geometry: tree.canopy, material: flatMaterial('#ffffff'), tinted: true },
      ],
      PER_TILE.trees,
      tiles,
    ),
    palms: new PropField(
      [
        { geometry: palm.trunk, material: flatMaterial('#b9b2a5') },
        { geometry: palm.fronds, material: flatMaterial('#3f7d3a') },
      ],
      PER_TILE.palms,
      tiles,
    ),
    lights: new PropField(
      [
        { geometry: light.pole, material: flatMaterial('#8d9399') },
        { geometry: light.heads, material: lampMaterial },
      ],
      PER_TILE.lights,
      tiles,
    ),
    umbrellas: new PropField(
      [
        { geometry: umbrella.canopy, material: flatMaterial('#ffffff'), tinted: true },
        { geometry: umbrella.base, material: flatMaterial('#6b4f3a') },
      ],
      PER_TILE.umbrellas,
      tiles,
    ),
    tanks: new PropField([{ geometry: polytankGeometry(), material: flatMaterial('#1c1c1c') }], PER_TILE.tanks, tiles),
  };
}

export function propMeshes(props: StreetProps) {
  return Object.values(props).flatMap((field: PropField) => field.meshes);
}
